// src/pages/inventory/master-data/material-category/MaterialCategory.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    IconButton,
    Stack,
    Snackbar,
    Button,
    TextField,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import { useTheme } from "@mui/material/styles";
import '../../../../components/css/ded-style.css';
import useAuthCheck from "../../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../../firebase";
import { ref, onValue, set, remove } from "firebase/database";
import * as XLSX from "xlsx";

// Import formatters dari file utilitas
import {
    formatText
} from "../../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../../utils/formatLastUpdated";

import CustomBreadcrumbs from "../../../../components/universal-features/breadcrumbs";

// Import CustomTableContainer dari komponen terpisah
import CustomTableContainer from "../../../../components/CustomScrollContainer";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper: generate ID otomatis
const generateAutoId = () => {
    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 1000);
    return `MAT-${timestamp}-${randomNum}`;
};

function MaterialCategory() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [editingRow, setEditingRow] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [lastUpdated, setLastUpdated] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [isAddIconRotated, setIsAddIconRotated] = useState(false);
    const [idError, setIdError] = useState('');
    const [namaError, setNamaError] = useState('');
    
    // State untuk data baru
    const [newData, setNewData] = useState({
        id_material: '',
        nama_kategori_material: ''
    });

    // Fungsi untuk mengecek apakah ID Material sudah ada (case-insensitive)
    const isIdMaterialExists = (idToCheck) => {
        if (!idToCheck || !originalDataList) return false;
        
        const idLower = idToCheck.toLowerCase().trim();
        return originalDataList.some(item => 
            item.id_material.toLowerCase().trim() === idLower
        );
    };

    // Fungsi untuk mengecek apakah Nama Kategori sudah ada (case-insensitive)
    const isNamaKategoriExists = (namaToCheck) => {
        if (!namaToCheck || !originalDataList) return false;
        
        const namaLower = namaToCheck.toLowerCase().trim();
        return originalDataList.some(item => 
            item.nama_kategori_material.toLowerCase().trim() === namaLower
        );
    };

    useEffect(() => {
        const fetchAllData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data
                await fetchMainData();
                
            } catch (error) {
                console.error("Error fetching all data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchAllData();
        }
    }, [user]);

    const fetchMainData = async () => {
        try {
            // Fetch main data from db_inventory/db_master_data/tb_material_category
            const materialCategoryRef = ref(database, "db_inventory/db_master_data/tb_material_category");
            const materialCategorySnapshot = await onValuePromise(materialCategoryRef);
            const materialCategoryData = materialCategorySnapshot.val();
            const loadedData = [];
            let latestUpdateTime = null;
            
            if (materialCategoryData) {
                Object.keys(materialCategoryData).forEach(key => {
                    const record = materialCategoryData[key];
                    loadedData.push({ 
                        firebaseKey: key, 
                        // Pastikan id_material selalu sama dengan firebaseKey
                        id_material: record.id_material || key,
                        nama_kategori_material: record.nama_kategori_material || '',
                        last_updated: record.last_updated || null
                    });
                    
                    // Cari waktu update terakhir
                    if (record.last_updated) {
                        const updateTime = new Date(record.last_updated);
                        if (!latestUpdateTime || updateTime > latestUpdateTime) {
                            latestUpdateTime = updateTime;
                        }
                    }
                });
                
                // Sort by id_material
                loadedData.sort((a, b) => a.id_material.localeCompare(b.id_material));
                
                // Set last updated time
                if (latestUpdateTime) {
                    setLastUpdated(latestUpdateTime);
                }
            }

            setOriginalDataList(loadedData);
            setDataList(loadedData);

        } catch (error) {
            console.error("Error fetching main data:", error);
            throw error;
        }
    };

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                item.id_material.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.nama_kategori_material.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            const reader = new FileReader();
            
            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });

                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    
                    // Baca data dari Excel
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                    
                    console.log("Data Excel:", jsonData);
                    
                    // Cari header
                    const headers = jsonData[0] || [];
                    console.log("Headers:", headers);
                    
                    // Data dimulai dari baris 2 (indeks 2 karena array mulai dari 0)
                    // Format: Kolom 0 = ID Material, Kolom 1 = Kategori Material
                    const dataRows = jsonData.slice(2);
                    console.log("Data rows:", dataRows);

                    // Path database
                    const dbPath = "db_inventory/db_master_data/tb_material_category";
                    const tbRef = ref(database, dbPath);

                    // Ambil data yang sudah ada
                    const snapshot = await onValuePromise(tbRef);
                    const existingData = snapshot.exists() ? snapshot.val() : {};

                    let importedCount = 0;
                    let updatedCount = 0;
                    let ignoredCount = 0;
                    const errors = [];

                    for (const row of dataRows) {
                        try {
                            // Skip baris kosong
                            if (!row || row.length === 0 || (!row[0] && !row[1])) {
                                ignoredCount++;
                                continue;
                            }

                            const idMaterial = row[0]?.toString().trim() || '';
                            const namaKategori = row[1]?.toString().trim() || '';

                            console.log("Processing row:", { idMaterial, namaKategori });

                            // Validasi: Nama Kategori wajib ada
                            if (!namaKategori) {
                                console.log("Nama Kategori kosong, diabaikan");
                                ignoredCount++;
                                continue;
                            }

                            // Jika ID Material tidak diisi, generate otomatis
                            let finalId = idMaterial;
                            if (!finalId) {
                                finalId = generateAutoId();
                            }

                            // Cek apakah ID Material sudah ada (case-insensitive)
                            const existingKey = Object.keys(existingData).find(key => 
                                key.toLowerCase().trim() === finalId.toLowerCase().trim()
                            );

                            // Cek apakah Nama Kategori sudah ada (case-insensitive)
                            const namaLower = namaKategori.toLowerCase().trim();
                            const existingByNama = Object.keys(existingData).find(key => 
                                existingData[key].nama_kategori_material?.toLowerCase().trim() === namaLower
                            );

                            // Persiapkan data untuk disimpan
                            // SIMPAN id_material sebagai field terpisah agar konsisten
                            const dataToSave = {
                                id_material: finalId, // Simpan id_material sebagai field
                                nama_kategori_material: namaKategori,
                                last_updated: new Date().toISOString()
                            };

                            console.log("Data yang akan disimpan:", { finalId, ...dataToSave });

                            if (existingKey) {
                                // Update data yang sudah ada (berdasarkan ID)
                                const existingItemRef = ref(database, `${dbPath}/${existingKey}`);
                                await set(existingItemRef, dataToSave);
                                updatedCount++;
                                console.log(`Data diperbarui: ${finalId} - ${namaKategori}`);
                            } else if (existingByNama) {
                                // Skip data dengan nama yang sama
                                console.log(`Data dengan nama "${namaKategori}" sudah ada, diabaikan`);
                                ignoredCount++;
                            } else {
                                // Simpan sebagai data baru
                                const newRef = ref(database, `${dbPath}/${finalId}`);
                                await set(newRef, dataToSave);
                                importedCount++;
                                console.log(`Data baru: ${finalId} - ${namaKategori}`);
                            }

                        } catch (error) {
                            console.error("Error processing row:", error, "Row data:", row);
                            errors.push(`Error pada baris: ${JSON.stringify(row)} - ${error.message}`);
                            ignoredCount++;
                        }
                    }

                    // Refresh data setelah import
                    await fetchMainData();

                    // Tampilkan hasil import
                    let message = `Import selesai! ${importedCount} data baru, ${updatedCount} data diperbarui.`;
                    let severity = "success";
                    
                    if (ignoredCount > 0) {
                        message += ` ${ignoredCount} baris diabaikan (data kosong/sudah ada).`;
                        severity = "warning";
                    }
                    
                    if (errors.length > 0) {
                        console.error("Import errors:", errors);
                    }

                    setSnackbar({
                        open: true,
                        message: message,
                        severity: severity,
                    });

                    console.log(`Import summary: ${importedCount} baru, ${updatedCount} diperbarui, ${ignoredCount} diabaikan`);

                } catch (error) {
                    console.error("Gagal mengimpor Excel:", error);
                    setSnackbar({
                        open: true,
                        message: `Gagal membaca file Excel: ${error.message}`,
                        severity: "error",
                    });
                } finally {
                    setImportLoading(false);
                }
            };

            reader.onerror = (error) => {
                console.error("Error reading file:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal membaca file.",
                    severity: "error",
                });
                setImportLoading(false);
            };

            reader.readAsArrayBuffer(file);

        } catch (error) {
            console.error("Error in import process:", error);
            setSnackbar({
                open: true,
                message: "Terjadi kesalahan saat memproses file.",
                severity: "error",
            });
            setImportLoading(false);
        } finally {
            event.target.value = null;
        }
    };

    const handleExport = () => {
        try {
            // Siapkan data untuk export
            const exportData = dataList.map(item => ({
                'ID Material': item.id_material,
                'Kategori Material': item.nama_kategori_material
            }));

            // Buat worksheet
            const ws = XLSX.utils.json_to_sheet(exportData);
            
            // Buat workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Material Category");
            
            // Simpan file
            XLSX.writeFile(wb, `Material_Category_${new Date().toISOString().split('T')[0]}.xlsx`);
            
            setSnackbar({
                open: true,
                message: "Data berhasil diexport.",
                severity: "success",
            });
            
        } catch (error) {
            console.error("Error exporting data:", error);
            setSnackbar({
                open: true,
                message: "Gagal mengexport data.",
                severity: "error",
            });
        }
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleEditClick = (row) => {
        setEditingRow(row.firebaseKey);
        setEditFormData({
            nama_kategori_material: row.nama_kategori_material
        });
    };

    const handleEditChange = (field, value) => {
        setEditFormData({
            ...editFormData,
            [field]: value
        });
    };

    const handleSaveEdit = async (firebaseKey) => {
        try {
            // Update data di Firebase
            const dataRef = ref(database, `db_inventory/db_master_data/tb_material_category/${firebaseKey}`);
            const updatedData = {
                id_material: firebaseKey, // Pastikan id_material sama dengan firebaseKey
                nama_kategori_material: editFormData.nama_kategori_material,
                last_updated: new Date().toISOString()
            };
            
            await set(dataRef, updatedData);
            
            // Update local state
            const updatedList = dataList.map(item => {
                if (item.firebaseKey === firebaseKey) {
                    return {
                        ...item,
                        ...updatedData,
                        last_updated: updatedData.last_updated
                    };
                }
                return item;
            });
            
            setDataList(updatedList);
            setOriginalDataList(updatedList);
            setEditingRow(null);
            
            setSnackbar({
                open: true,
                message: "Data berhasil diperbarui.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error updating data:", error);
            setSnackbar({
                open: true,
                message: "Gagal memperbarui data.",
                severity: "error",
            });
        }
    };

    const handleCancelEdit = () => {
        setEditingRow(null);
        setEditFormData({});
    };

    const handleDelete = async (firebaseKey, idMaterial) => {
        if (window.confirm(`Apakah Anda yakin ingin menghapus data dengan ID Material: ${idMaterial}?`)) {
            try {
                // Hapus data dari Firebase
                const dataRef = ref(database, `db_inventory/db_master_data/tb_material_category/${firebaseKey}`);
                await remove(dataRef);
                
                // Update local state
                const updatedList = dataList.filter(item => item.firebaseKey !== firebaseKey);
                setDataList(updatedList);
                setOriginalDataList(updatedList);
                
                setSnackbar({
                    open: true,
                    message: "Data berhasil dihapus.",
                    severity: "success",
                });
                
                // Update last updated time
                setLastUpdated(new Date());
                
            } catch (error) {
                console.error("Error deleting data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal menghapus data.",
                    severity: "error",
                });
            }
        }
    };

    const handleAddNew = () => {
        setIsAddIconRotated(!isAddIconRotated);
        setShowAddForm(!showAddForm);
        // Reset form ketika membuka form
        if (!showAddForm) {
            setNewData({
                id_material: '',
                nama_kategori_material: ''
            });
            setIdError('');
            setNamaError('');
        }
    };

    const handleNewDataChange = (field, value) => {
        setNewData({
            ...newData,
            [field]: value
        });
        
        // Reset error saat user mulai mengetik
        if (field === 'id_material') {
            setIdError('');
        }
        if (field === 'nama_kategori_material') {
            setNamaError('');
        }
    };

    const handleSaveNew = async () => {
        // Reset error messages
        setIdError('');
        setNamaError('');
        
        let hasError = false;

        // Validasi: Nama Kategori Material tidak boleh kosong
        if (!newData.nama_kategori_material.trim()) {
            setNamaError('Kategori Material tidak boleh kosong.');
            hasError = true;
        }

        // Cek apakah Nama Kategori sudah ada (case-insensitive)
        if (newData.nama_kategori_material.trim() && isNamaKategoriExists(newData.nama_kategori_material)) {
            setNamaError(`Kategori "${newData.nama_kategori_material}" sudah ada dalam database.`);
            hasError = true;
        }

        // Jika ID Material diisi, cek apakah sudah ada
        if (newData.id_material.trim() && isIdMaterialExists(newData.id_material)) {
            setIdError(`ID Material "${newData.id_material}" sudah ada dalam database.`);
            hasError = true;
        }

        if (hasError) {
            return;
        }

        try {
            // Tentukan ID: gunakan ID yang diisi atau generate otomatis
            const finalId = newData.id_material.trim() ? newData.id_material : generateAutoId();
            
            // Simpan ke Firebase dengan key = finalId
            const dataRef = ref(database, `db_inventory/db_master_data/tb_material_category/${finalId}`);
            const dataToSave = {
                id_material: finalId, // Simpan id_material sebagai field
                nama_kategori_material: newData.nama_kategori_material,
                last_updated: new Date().toISOString()
            };
            
            await set(dataRef, dataToSave);
            
            // Update local state
            const newItem = {
                firebaseKey: finalId,
                id_material: finalId, // Pastikan id_material = firebaseKey
                ...dataToSave
            };
            
            const updatedList = [...originalDataList, newItem].sort((a, b) => 
                a.id_material.localeCompare(b.id_material)
            );
            
            setDataList(updatedList);
            setOriginalDataList(updatedList);
            
            // Reset form dan state
            setNewData({
                id_material: '',
                nama_kategori_material: ''
            });
            setShowAddForm(false);
            setIsAddIconRotated(false);
            
            setSnackbar({
                open: true,
                message: "Data berhasil ditambahkan.",
                severity: "success",
            });
            
            // Update last updated time
            setLastUpdated(new Date());
            
        } catch (error) {
            console.error("Error adding data:", error);
            setSnackbar({
                open: true,
                message: "Gagal menambahkan data.",
                severity: "error",
            });
        }
    };

    const handleCancelNew = () => {
        setShowAddForm(false);
        setIsAddIconRotated(false);
        setNewData({
            id_material: '',
            nama_kategori_material: ''
        });
        setIdError('');
        setNamaError('');
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Material Category
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        {/* IconButton Plus/X untuk Tambah Data */}
                        <IconButton 
                            color="primary" 
                            size="small" 
                            onClick={handleAddNew}
                            sx={{
                                transition: 'transform 0.3s ease-in-out',
                                transform: isAddIconRotated ? 'rotate(45deg)' : 'rotate(0deg)',
                                backgroundColor: isAddIconRotated ? '#f2f9ff' : 'transparent',
                                '&:hover': {
                                    backgroundColor: isAddIconRotated ? '#f2f9ff' : 'action.hover'
                                }
                            }}
                        >
                            <AddOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Form Tambah Data Baru */}
                {showAddForm && (
                    <>
                        <Paper sx={{ p: 2, mb: 2, bgcolor: '#f2f9ff' }} elevation={0}>
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                Tambah Data Baru
                            </Typography>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                                <TextField
                                    label="ID Material"
                                    size="small"
                                    value={newData.id_material}
                                    onChange={(e) => handleNewDataChange('id_material', e.target.value)}
                                    fullWidth
                                    placeholder="Opsional - kosongkan untuk generate otomatis"
                                    error={!!idError}
                                />
                                <TextField
                                    label="Kategori Material"
                                    size="small"
                                    value={newData.nama_kategori_material}
                                    onChange={(e) => handleNewDataChange('nama_kategori_material', e.target.value)}
                                    required
                                    fullWidth
                                    error={!!namaError}
                                />
                            </Stack>
                            {/* Error messages di bawah form */}
                            <Box sx={{ mt: 1, mb: 2 }}>
                                {idError && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5 }}>
                                        {idError}
                                    </Typography>
                                )}
                                {namaError && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                        {namaError}
                                    </Typography>
                                )}
                                {!idError && !namaError && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        ID Material opsional. Kosongkan untuk generate otomatis.
                                    </Typography>
                                )}
                            </Box>
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button variant="outlined" onClick={handleCancelNew}>
                                    Batal
                                </Button>
                                <Button variant="contained" onClick={handleSaveNew}>
                                    Simpan
                                </Button>
                            </Stack>
                        </Paper>
                    </>
                )}

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="data kategori material table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className="ded-tch">No</TableCell>
                                        <TableCell className="ded-tch">ID Material</TableCell>
                                        <TableCell className="ded-tch">Kategori Material</TableCell>
                                        <TableCell className="ded-tch" style={{ width: '100px' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index) => (
                                            <TableRow key={row.firebaseKey}>
                                                {editingRow === row.firebaseKey ? (
                                                    <>
                                                        <TableCell>
                                                            {page * rowsPerPage + index + 1}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {row.id_material}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                value={editFormData.nama_kategori_material}
                                                                onChange={(e) => handleEditChange('nama_kategori_material', e.target.value)}
                                                                fullWidth
                                                                error={isNamaKategoriExists(editFormData.nama_kategori_material)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="success"
                                                                    onClick={() => handleSaveEdit(row.firebaseKey)}
                                                                    disabled={isNamaKategoriExists(editFormData.nama_kategori_material)}
                                                                >
                                                                    <CheckOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={handleCancelEdit}
                                                                >
                                                                    <CloseOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        <TableCell>
                                                            {page * rowsPerPage + index + 1}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box>
                                                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                    {formatText(row.id_material)}
                                                                </div>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>{formatText(row.nama_kategori_material)}</TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="primary"
                                                                    onClick={() => handleEditClick(row)}
                                                                >
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    size="small" 
                                                                    color="error"
                                                                    onClick={() => handleDelete(row.firebaseKey, row.id_material)}
                                                                >
                                                                    <DeleteOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data. Klik icon "+" untuk menambahkan atau "Import Excel" untuk mengimpor data.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default MaterialCategory;